import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Users, AtSign, Paperclip, X } from "lucide-react";
import aapi, { formatApiError } from "./aapi";
import { PageHeader, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

export default function AdminSendMail() {
  const [mode, setMode] = useState("manual");
  const [recipients, setRecipients] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attach, setAttach] = useState(null); // { name, b64, size }

  const onAttach = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf"))
      return toast.error("Sirf PDF file attach ho sakti hai");
    if (f.size > 3 * 1024 * 1024) return toast.error("PDF 3MB se choti honi chahiye");
    const r = new FileReader();
    r.onload = () => {
      setAttach({ name: f.name, b64: String(r.result).split(",")[1], size: f.size });
      toast.success(`"${f.name}" attach ho gayi`);
    };
    r.readAsDataURL(f);
  };
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const list = recipients.split(/[\n,;]+/).map((e) => e.trim()).filter(Boolean);

  const send = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await aapi.post("/send-mail", {
        recipients: mode === "manual" ? list : [],
        send_to_all: mode === "all",
        subject: subject.trim(),
        body: body.trim(),
        ...(attach ? { attachment_b64: attach.b64, attachment_name: attach.name } : {}),
      });
      setResult(data);
      if (data.background) toast.success(`${data.queued} recipients — background mein deliver ho rahi hai${data.skipped_disabled ? ` · ${data.skipped_disabled} disabled skipped` : ""}`);
      else if (data.failed > 0) toast.warning(`${data.sent} delivered, ${data.failed} failed`);
      else toast.success(`Mail delivered — ${data.sent} recipient(s)${data.skipped_disabled ? ` · ${data.skipped_disabled} disabled users skipped` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const canSend = subject.trim().length >= 3 && body.trim().length >= 5 && !busy && (mode === "all" || list.length > 0);

  const pill = (id, label, Icon, tid) => (
    <button type="button" onClick={() => { setMode(id); setResult(null); }} data-testid={tid}
      className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-3 text-xs font-semibold transition-all duration-300 ${
        mode === id ? "border-gold bg-gold/10 text-goldlight" : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
      }`}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div data-testid="admin-send-mail-page">
      <PageHeader title="Send Mail" sub="Simple plain mail — koi template nahi, koi branding nahi, koi document number nahi. Jaise likhoge, waise hi jayegi." />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
        className="max-w-2xl rounded-3xl border border-gold/20 bg-panel p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
            <Send size={18} className="text-gold" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">PLAIN CUSTOM MAIL</div>
            <p className="mt-1 text-xs text-neutral-500">Promotional ya koi bhi custom mail — ek-ek address ko ya saare customers ko, aap decide karo.</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Recipients</label>
            <div className="mt-2 flex gap-2">
              {pill("manual", "Manual emails", AtSign, "mail-mode-manual")}
              {pill("all", "All registered customers", Users, "mail-mode-all")}
            </div>
          </div>

          {mode === "manual" ? (
            <div>
              <textarea rows={3} value={recipients} onChange={(e) => { setRecipients(e.target.value); setResult(null); }}
                placeholder="Emails yahan likhein — comma ya new line se alag karein&#10;e.g. user1@gmail.com, user2@yahoo.com"
                className={`${inputCls} resize-y font-mono text-xs`} data-testid="mail-recipients-input" />
              {list.length > 0 && (
                <p className="mt-1.5 font-mono text-[10px] text-neutral-500" data-testid="mail-recipient-count">{list.length} recipient(s)</p>
              )}
            </div>
          ) : (
            <p className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[11px] leading-relaxed text-amber-300" data-testid="mail-all-warning">
              Ye mail SAARE registered customers ko jayegi — <strong>disabled users automatically exclude</strong> ho jayenge. 10 se zyada recipients par mail background mein bheji jaati hai — subject aur message dhyan se likhein.
            </p>
          )}

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Subject</label>
            <input value={subject} onChange={(e) => { setSubject(e.target.value); setResult(null); }} maxLength={140}
              placeholder="Bilkul waisa subject jaisa recipient ko dikhna chahiye" className={`${inputCls} mt-2`} data-testid="mail-subject-input" />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Message</label>
            <textarea rows={9} value={body} onChange={(e) => { setBody(e.target.value); setResult(null); }}
              placeholder="Apna message likhein — line breaks waise hi rahenge. Koi header, logo, footer ya BK number add nahi hoga."
              className={`${inputCls} mt-2 resize-y text-sm`} data-testid="mail-body-input" />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">PDF attachment (optional)</label>
            {attach ? (
              <div className="mt-2 flex items-center justify-between rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-3" data-testid="mail-attachment-chip">
                <span className="flex min-w-0 items-center gap-2 text-xs text-goldlight">
                  <Paperclip size={13} className="shrink-0" />
                  <span className="truncate">{attach.name}</span>
                  <span className="shrink-0 font-mono text-[9px] text-neutral-500">({(attach.size / 1024).toFixed(0)} KB)</span>
                </span>
                <button onClick={() => setAttach(null)} aria-label="Remove attachment" data-testid="mail-attachment-remove"
                  className="shrink-0 text-neutral-500 transition-colors hover:text-rose-300">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 px-4 py-3.5 text-xs text-neutral-400 transition-colors hover:border-gold/50 hover:text-goldlight"
                data-testid="mail-attachment-button">
                <Paperclip size={14} /> Custom PDF attach karein — max 3MB
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onAttach} data-testid="mail-attachment-input" />
              </label>
            )}
            <p className="mt-1.5 font-mono text-[9px] tracking-[0.08em] text-neutral-600">
              Invoice, rate card, ya koi bhi document — har recipient ko attach hokar jayega
            </p>
          </div>

          <button onClick={send} disabled={!canSend} data-testid="mail-send-button"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
            {busy && <Loader2 size={15} className="animate-spin" />}
            SEND PLAIN MAIL{mode === "all" ? " TO ALL CUSTOMERS" : list.length > 0 ? ` TO ${list.length} RECIPIENT(S)` : ""}
          </button>

          {result && (
            <div className="rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-4" data-testid="mail-result">
              {result.background ? (
                <>
                  <span className="rounded-full bg-amber-400/15 px-2.5 py-1 font-mono text-[9px] tracking-[0.12em] text-amber-300" data-testid="mail-result-summary">
                    QUEUED — {result.queued} RECIPIENTS · BACKGROUND
                  </span>
                  <p className="mt-2 text-[11px] leading-relaxed text-neutral-400">
                    Mails background mein bheji ja rahi hain. Final sent/failed count Audit Log aur system records mein save hoga.
                  </p>
                </>
              ) : (
                <>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[9px] tracking-[0.12em] ${result.failed > 0 ? "bg-amber-400/15 text-amber-300" : "bg-emerald-400/15 text-emerald-300"}`} data-testid="mail-result-summary">
                    DELIVERED: {result.sent}{result.failed > 0 ? ` · FAILED: ${result.failed}` : ""}
                  </span>
                  {result.failed > 0 && (
                    <p className="mt-2 font-mono text-[10px] leading-relaxed text-neutral-500" data-testid="mail-failed-list">
                      Failed: {result.failed_list.join(", ")}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
